import { useState } from 'react';

const OKTA_SCOPES_READ = [
  { name: 'okta.users.read', description: 'Read user profiles and status' },
  { name: 'okta.groups.read', description: 'Read group memberships' },
  { name: 'okta.apps.read', description: 'Read application assignments' },
  { name: 'okta.logs.read', description: 'Read system log events' },
  { name: 'okta.policies.read', description: 'Read security policies' },
  { name: 'okta.factors.read', description: 'Read MFA factor enrollments' },
] as const;

const OKTA_SCOPES_WRITE = [
  { name: 'okta.apps.manage', description: 'Deactivate OAuth apps and revoke consent grants' },
] as const;

const ALL_OKTA_SCOPES = [...OKTA_SCOPES_READ, ...OKTA_SCOPES_WRITE];

interface Props {
  onSubmit: (config: Record<string, unknown>) => void;
  isPending: boolean;
  error: string;
}

function validateJwk(text: string): boolean {
  try {
    const parsed = JSON.parse(text);
    return typeof parsed === 'object' && parsed !== null && 'kty' in parsed;
  } catch {
    return false;
  }
}

export function OktaWizard({ onSubmit, isPending, error }: Props) {
  const [step, setStep] = useState(1);
  const [domain, setDomain] = useState('');
  const [clientId, setClientId] = useState('');
  const [privateKeyJwk, setPrivateKeyJwk] = useState('');
  const [keyError, setKeyError] = useState('');
  const [keyPasteText, setKeyPasteText] = useState('');

  const keyLoaded = privateKeyJwk !== '' && validateJwk(privateKeyJwk);

  function handlePaste(value: string) {
    setKeyPasteText(value);
    setKeyError('');
    if (value.trim() && validateJwk(value.trim())) {
      setPrivateKeyJwk(value.trim());
    }
  }

  function resetKey() {
    setPrivateKeyJwk('');
    setKeyPasteText('');
    setKeyError('');
  }

  function handleConnect() {
    onSubmit({
      domain,
      clientId,
      privateKeyJwk,
    });
  }

  const canContinue: Record<number, boolean> = {
    1: domain.trim() !== '' && clientId.trim() !== '',
    2: keyLoaded,
    3: true,
    4: true,
  };

  const stepLabels = ['Create App', 'Private Key', 'Grant Scopes', 'Connect'];

  return (
    <>
      {/* Step indicator */}
      <div className="wizard-steps">
        {stepLabels.map((label, i) => {
          const num = i + 1;
          const done = num < step;
          const active = num === step;
          return (
            <div key={num} style={{ display: 'flex', alignItems: 'center' }}>
              {i > 0 && <div className={`wizard-step-line${done ? ' done' : ''}`} />}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div className={`wizard-step-circle${done ? ' done' : ''}${active ? ' active' : ''}`}>
                  {done ? '✓' : num}
                </div>
                <span className="wizard-step-label">{label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Step content */}
      <div className="wizard-body">
        {step === 1 && (
          <>
            <div className="wizard-callout">
              <strong>Prerequisites:</strong> You need <strong>Super Admin</strong> or <strong>Org Admin</strong> role in Okta to complete this setup.
            </div>
            <div className="form-field">
              <label htmlFor="wiz-domain">Okta Domain</label>
              <input
                id="wiz-domain"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="your-org.okta.com"
              />
            </div>
            <ol style={{ fontSize: 13, color: 'var(--color-text-secondary)', paddingLeft: 20, margin: '12px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <li>Go to <strong>Okta Admin Console → Applications → Create App Integration → API Services</strong></li>
              <li>Name the app <strong>"Cade"</strong></li>
              <li>Go to <strong>Admin Roles</strong> tab → Edit assignments → assign <strong>Read-Only Administrator</strong></li>
              <li>Copy the <strong>Client ID</strong> from the General tab</li>
            </ol>
            <div className="form-field">
              <label htmlFor="wiz-client-id">Client ID</label>
              <input
                id="wiz-client-id"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="0oa..."
                autoComplete="off"
              />
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="wizard-callout warning">
              <strong>Important:</strong> The private key is shown only once when generated in Okta. Store it securely.
            </div>
            <ol style={{ fontSize: 13, color: 'var(--color-text-secondary)', paddingLeft: 20, margin: '12px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <li>In the Cade app → <strong>General</strong> tab → Client Credentials → <strong>Edit</strong></li>
              <li>Change to <strong>Public key / Private key</strong></li>
              <li>Generate a new key under <strong>Public keys</strong></li>
              <li>Make sure you click <strong>Save</strong> under both Public keys and Client credentials</li>
            </ol>

            {keyLoaded ? (
              <div className="key-dropzone has-key">
                <span style={{ color: 'var(--sev-success)', fontWeight: 500 }}>
                  ✓ Private key loaded
                </span>
                <button
                  type="button"
                  onClick={resetKey}
                  style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: 12, textDecoration: 'underline', marginTop: 4 }}
                >
                  Replace key
                </button>
              </div>
            ) : (
              <div className="form-field">
                <label htmlFor="wiz-private-key">Paste the private key (JWK format)</label>
                <textarea
                  id="wiz-private-key"
                  value={keyPasteText}
                  onChange={(e) => handlePaste(e.target.value)}
                  placeholder='{"kty":"RSA","n":"...","e":"...","d":"..."}'
                  rows={5}
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
                />
              </div>
            )}

            {keyError && <p style={{ color: 'var(--color-error)', fontSize: 13, margin: 0 }}>{keyError}</p>}
          </>
        )}

        {step === 3 && (
          <>
            <ol style={{ fontSize: 13, color: 'var(--color-text-secondary)', paddingLeft: 20, margin: '0 0 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <li>In the Cade app, go to the <strong>Okta API Scopes</strong> tab</li>
              <li>Find each of the following scopes and click <strong>Grant</strong></li>
            </ol>
            <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-primary)', margin: '8px 0 4px' }}>
              Read scopes
            </p>
            <div className="scope-list">
              {OKTA_SCOPES_READ.map((s) => (
                <div key={s.name} className="scope-item">
                  <span style={{ color: 'var(--sev-success)', fontSize: 14 }}>✓</span>
                  <span className="scope-item-name">{s.name}</span>
                  <span className="scope-item-desc">{s.description}</span>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-primary)', margin: '12px 0 4px' }}>
              Write scopes <span style={{ color: 'var(--color-text-muted)', fontSize: 11, fontWeight: 400 }}>(required for response actions)</span>
            </p>
            <div className="scope-list">
              {OKTA_SCOPES_WRITE.map((s) => (
                <div key={s.name} className="scope-item">
                  <span style={{ color: 'var(--sev-success)', fontSize: 14 }}>✓</span>
                  <span className="scope-item-name">{s.name}</span>
                  <span className="scope-item-desc">{s.description}</span>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '12px 0 0' }}>
              {ALL_OKTA_SCOPES.length} scopes — {OKTA_SCOPES_READ.length} read + {OKTA_SCOPES_WRITE.length} write
            </p>
          </>
        )}

        {step === 4 && (
          <>
            <div className="wizard-summary">
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">Domain</span>
                <span>{domain}</span>
              </div>
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">Client ID</span>
                <span>{clientId.slice(0, 8)}{'•'.repeat(Math.max(0, clientId.length - 8))}</span>
              </div>
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">Private Key</span>
                <span style={{ color: 'var(--sev-success)' }}>✓ Provided</span>
              </div>
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">Auth Method</span>
                <span>Private Key JWT (RS256)</span>
              </div>
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">Scopes</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {ALL_OKTA_SCOPES.map((s) => (
                    <span key={s.name} className="scope-pill">{s.name}</span>
                  ))}
                </div>
              </div>
            </div>

            {error && <p className="login-error">{error}</p>}
          </>
        )}
      </div>

      {/* Navigation */}
      <div className="wizard-nav">
        <div>
          {step > 1 && (
            <button type="button" className="btn-secondary" onClick={() => setStep(step - 1)}>
              Back
            </button>
          )}
        </div>
        <div>
          {step < 4 ? (
            <button
              type="button"
              className="btn-primary"
              disabled={!canContinue[step]}
              onClick={() => setStep(step + 1)}
            >
              {step === 3 ? "I've granted all scopes" : 'Continue'}
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary"
              disabled={isPending}
              onClick={handleConnect}
            >
              {isPending ? 'Connecting...' : 'Connect'}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
